import { Icon } from "../components/Icon";

type SeoLandingPageProps = {
  go: (route: string, params?: Record<string, string>) => void;
};

const features = [
  {
    title: "Этичные объявления",
    text: "Каталог для вещей, услуг и проектов, которые выбирают осознанно: без лишней агрессии, обмана и небезопасных предложений.",
    icon: "ethical-shield"
  },
  {
    title: "Товары рядом",
    text: "Поиск по России, регионам и городам помогает находить предложения поблизости или смотреть все города сразу.",
    icon: "sprout-simple"
  },
  {
    title: "Покупка и продажа",
    text: "Можно разместить объявление, добавить фото, выбрать категорию, написать продавцу и сохранить интересные варианты.",
    icon: "ethical-users"
  },
  {
    title: "Осознанный выбор",
    text: "PremaMarket подходит для личных вещей, товаров для дома, услуг, работы, транспорта, электроники и добрых инициатив.",
    icon: "ethical-leaf"
  }
] as const;

const categories = [
  "Личные вещи",
  "Для дома и дачи",
  "Услуги",
  "Транспорт",
  "Работа",
  "Электроника",
  "Красота и здоровье",
  "Товары для детей",
  "Продукты",
  "Хобби и отдых"
];

export function SeoLandingPage({ go }: SeoLandingPageProps) {
  return (
    <div className="seo-page">
      <section className="seo-hero">
        <div className="seo-hero-copy">
          <p className="seo-eyebrow">Этичный маркетплейс объявлений</p>
          <h1>PremaMarket - объявления онлайн для осознанных покупок и продаж</h1>
          <p>
            PremaMarket помогает находить товары и услуги с заботой о себе, людях, животных и планете.
            Здесь можно купить, продать или разместить объявление в понятных категориях и связаться с продавцом напрямую.
          </p>
          <div className="seo-actions">
            <button className="btn btn-primary" type="button" onClick={() => go("catalog")}>Смотреть каталог</button>
            <button className="btn btn-ghost" type="button" onClick={() => go("listing.new", { reason: "listing" })}>+ Разместить объявление</button>
          </div>
        </div>
        <div className="seo-hero-panel" aria-label="Основные направления PremaMarket">
          <strong>Что можно найти</strong>
          <ul>
            <li>личные вещи, одежду, товары для дома и дачи;</li>
            <li>услуги, работу, транспорт и электронику;</li>
            <li>веганские, экологичные и бережные предложения;</li>
            <li>объявления рядом с вами или по всей России.</li>
          </ul>
        </div>
      </section>

      <section className="seo-feature-grid" aria-label="Преимущества PremaMarket">
        {features.map((feature) => (
          <article className="seo-feature" key={feature.title}>
            <Icon name={feature.icon} />
            <h2>{feature.title}</h2>
            <p>{feature.text}</p>
          </article>
        ))}
      </section>

      <section className="seo-section">
        <div>
          <p className="seo-eyebrow">Каталог объявлений</p>
          <h2>Маркетплейс для товаров, услуг и добрых инициатив</h2>
          <p>
            На PremaMarket можно искать объявления по категориям, городу, стране, цене и состоянию товара.
            Площадка подходит для частных продавцов, мастеров, специалистов и небольших этичных проектов.
          </p>
        </div>
        <div className="seo-category-cloud">
          {categories.map((category) => (
            <button type="button" onClick={() => go("catalog")} key={category}>{category}</button>
          ))}
        </div>
      </section>

      <section className="seo-faq">
        <h2>PremaMarket в поиске Google и Яндекс</h2>
        <details open>
          <summary>Что такое PremaMarket?</summary>
          <p>
            PremaMarket - это этичный маркетплейс объявлений, где пользователи размещают товары и услуги,
            ищут предложения рядом с собой, связываются друг с другом и договариваются напрямую.
          </p>
          <p>
            Площадка создана для осознанного обмена: здесь важны доверие, уважительное общение,
            ответственность участников и принцип ахимсы - ненасилия и бережного отношения к людям,
            животным, насекомым и природе.
          </p>
          <p>
            PremaMarket подходит вегетарианцам, веганам, фрукторианцам, сыроедам и всем, кто выбрал
            путь ненасилия и хочет искать товары без жестокости, эксплуатации и причинения вреда живым существам.
          </p>
        </details>
        <details>
          <summary>Можно ли размещать объявления бесплатно?</summary>
          <p>На этапе MVP размещение объявлений доступно через аккаунт. Пользователь заполняет название, категорию, описание, цену, контакты и добавляет фотографии.</p>
        </details>
        <details>
          <summary>Как найти объявления в своем городе?</summary>
          <p>В поиске можно выбрать страну и город или оставить значение «Все города», чтобы смотреть предложения шире.</p>
        </details>
        <details>
          <summary>Почему площадка называется этичной?</summary>
          <p>
            PremaMarket называется этичной площадкой, потому что придерживается принципа ахимсы.
            На сайте не допускаются предложения, связанные с убийством, эксплуатацией или причинением вреда
            людям, животным и насекомым.
          </p>
          <p>
            Поэтому модерация ограничивает товары из натуральной кожи, меха, шкур, перьев, пуха,
            кости, рога, панцирей, а также продукты и ингредиенты животного или насекомого происхождения:
            мясо, рыбу, морепродукты, яйца, желатин, кармин, шеллак и похожие позиции.
          </p>
          <p>
            Такая этичность помогает покупателям и продавцам быстрее находить предложения,
            которые соответствуют вегетарианскому, веганскому и более широкому пути ненасилия.
          </p>
        </details>
      </section>
    </div>
  );
}
